/**
 * UsageContext
 *
 * Fetches the signed-in user's plan/usage state once per session and shares
 * it across every consumer (sidebar widget, Usage page, Plan & billing page)
 * so navigating between dashboard pages doesn't re-fetch or re-flash loading
 * state.
 *
 * Usage:
 *   // Wrap the authenticated app root:
 *   <UsageProvider><App /></UsageProvider>
 *
 *   // In any component:
 *   const { usage, monthlyHistory, loading, refresh } = useUsage();
 */

import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { DASHBOARD_FLAGS_OFF, type DashboardFlags } from "../../../utils/billing-shared";
import { getUsageUrl } from "../utils/billingUrls";
import { isCheckoutSignal } from "../utils/checkoutSignal";
import { setUserProperties } from "../utils/posthogConfig";
import { readSessionCache, writeSessionCache } from "../utils/sessionCache";
import { useAuth } from "./AuthContext";

export interface UsageState {
    plan: "free" | "pro";
    callsUsed: number;
    callsIncluded: number | null; // null = unlimited
    resetsAt: string;
    pct: number | null; // null when unlimited
}

export interface MonthlyUsage {
    month: string; // 'YYYY-MM-01'
    calls: number;
}

export interface ConversionContext {
    plan: "free" | "pro";
    calls_used: number;
    calls_limit: number | null;
    pct_used: number | null;
    days_since_signup: number;
    client: "chatgpt" | "claude" | "cursor" | "other";
    device_count: number;
    online_device_count: number;
}

interface UsageContextType {
    usage: UsageState | null;
    monthlyHistory: MonthlyUsage[] | null;
    loading: boolean;
    refresh: () => void;
    /** Brief UI state while a checkout window/navigation is being opened. It
     * is deliberately not persisted: abandoned Stripe sessions are replaceable
     * and must never lock the dashboard. */
    checkoutStarted: boolean;
    setCheckoutStarted: (started: boolean) => void;
    /**
     * Server-computed: whether the subscription surface (nav item, upgrade
     * CTAs, Plan & billing page) should show at all. False while the usage
     * gate is off/shadow for this user, unless they have Pro access —
     * subscribers must still be able to manage their subscriptions.
     * Defaults to false (matches the pre-load `usage === null` state) so
     * nothing flashes visible before the real value arrives.
     */
    billingVisible: boolean;
    hasInvoices: boolean;
    conversionContext: ConversionContext | null;
    dashboardFlags: DashboardFlags;
    /** The usage gate's warn_at_pct: from this percentage a free user is warned about the limit. */
    warnAtPct: number;
    /** The reset date the usage warning was dismissed for. Shared so the banner and the Usage
     * page react to a dismissal together. */
    usageWarningDismissedFor: string | null;
    setUsageWarningDismissedFor: (resetsAt: string) => void;
}

type UsageResponse = {
    usage: UsageState | null;
    monthlyHistory: MonthlyUsage[] | null;
    billingVisible?: boolean;
    hasInvoices?: boolean;
    conversionContext?: ConversionContext | null;
    dashboardFlags?: DashboardFlags | null;
    warnAtPct?: number;
};

// Per-tab cache, same pattern as SubscriptionCard's readCachedSubscription —
// shows the last known plan/usage instantly on a reload instead of a loading
// flash, while the fetch below still runs in the background and corrects it
// if stale. The cached value records its owning user so signing out and back
// in as another account in the same tab can never paint the previous account's
// plan or usage while the fresh request is loading.
const USAGE_CACHE_KEY = "desktop-commander:usage-cache";

type CachedUsage = {
    userId: string;
    usage: UsageState | null;
    monthlyHistory: MonthlyUsage[] | null;
    billingVisible: boolean;
    hasInvoices: boolean;
    conversionContext: ConversionContext | null;
    /** Optional: caches written before flags existed don't have it. */
    dashboardFlags?: DashboardFlags;
    warnAtPct?: number;
};

// The usage gate's own default, for a response or cache entry without warnAtPct.
const DEFAULT_WARN_AT_PCT = 80;

const readCachedUsage = () => readSessionCache<CachedUsage>(USAGE_CACHE_KEY);
const writeCachedUsage = (userId: string, value: Omit<CachedUsage, "userId">) =>
    writeSessionCache(USAGE_CACHE_KEY, {
        userId,
        ...value,
    });
const UsageContext = createContext<UsageContextType | null>(null);

export function useUsage() {
    const context = useContext(UsageContext);
    if (!context) {
        throw new Error("useUsage must be used within a UsageProvider");
    }
    return context;
}

export function UsageProvider({ children }: { children: ReactNode }) {
    const { user, isSessionChecked } = useAuth();
    // Read the cached blob ONCE per mount (lazy initializer — never re-runs on
    // re-render) and reuse it below; four separate readCachedUsage() calls
    // would each independently re-read and re-parse the same sessionStorage
    // entry for no reason.
    const [initialCache] = useState(readCachedUsage);
    const [usage, setUsage] = useState<UsageState | null>(initialCache?.usage ?? null);
    const [monthlyHistory, setMonthlyHistory] = useState<MonthlyUsage[] | null>(initialCache?.monthlyHistory ?? null);
    // No cache yet (first load this tab) is the only case that still shows a
    // loading state — a cache hit paints immediately while the fetch below
    // corrects it in the background.
    const [loading, setLoading] = useState(initialCache === null);
    const [refreshCount, setRefreshCount] = useState(0);
    const [checkoutStarted, setCheckoutStartedState] = useState(false);
    const [billingVisible, setBillingVisible] = useState(initialCache?.billingVisible ?? false);
    const [hasInvoices, setHasInvoices] = useState(initialCache?.hasInvoices ?? false);
    const [dashboardFlags, setDashboardFlags] = useState<DashboardFlags>(initialCache?.dashboardFlags ?? DASHBOARD_FLAGS_OFF);
    const [conversionContext, setConversionContext] = useState<ConversionContext | null>(initialCache?.conversionContext ?? null);
    const [warnAtPct, setWarnAtPct] = useState(initialCache?.warnAtPct ?? DEFAULT_WARN_AT_PCT);
    const [usageWarningDismissedFor, setUsageWarningDismissedFor] = useState<string | null>(null);
    const [dataUserId, setDataUserId] = useState<string | null>(initialCache?.userId ?? null);
    const dataUserIdRef = useRef<string | null>(initialCache?.userId ?? null);
    const hasCurrentUserData = user !== null && dataUserId === user.id;

    useEffect(() => {
        // Auth starts with user=null while it restores the session. Waiting for
        // that check preserves a matching cache across reloads without ever
        // exposing it before its owner is known. Not `loading`: that flips on
        // every background session poll and would refetch usage each time.
        if (!isSessionChecked) {
            return;
        }

        if (!user) {
            setUsage(null);
            setMonthlyHistory(null);
            setBillingVisible(false);
            setHasInvoices(false);
            setDashboardFlags(DASHBOARD_FLAGS_OFF);
            setConversionContext(null);
            setWarnAtPct(DEFAULT_WARN_AT_PCT);
            setDataUserId(null);
            dataUserIdRef.current = null;
            setLoading(false);
            return;
        }

        const controller = new AbortController();
        const cached = readCachedUsage();
        const matchingCache = cached?.userId === user.id ? cached : null;
        const alreadyHasCurrentUserData = dataUserIdRef.current === user.id;

        if (!alreadyHasCurrentUserData) {
            setUsage(matchingCache?.usage ?? null);
            setMonthlyHistory(matchingCache?.monthlyHistory ?? null);
            setBillingVisible(matchingCache?.billingVisible ?? false);
            setHasInvoices(matchingCache?.hasInvoices ?? false);
            setDashboardFlags(matchingCache?.dashboardFlags ?? DASHBOARD_FLAGS_OFF);
            setConversionContext(matchingCache?.conversionContext ?? null);
            setWarnAtPct(matchingCache?.warnAtPct ?? DEFAULT_WARN_AT_PCT);
            setDataUserId(matchingCache?.userId ?? null);
            dataUserIdRef.current = matchingCache?.userId ?? null;
        }

        // Only show the loading state when there's nothing to show yet — a
        // background revalidation of already-cached (or previously-fetched)
        // usage must not flash consumers back to "loading" while it runs.
        const hasDataToShow = alreadyHasCurrentUserData || matchingCache !== null;
        setLoading(!hasDataToShow);
        fetch(getUsageUrl(), {
            credentials: "include",
            signal: controller.signal,
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error(`Failed to load usage (${response.status})`);
                }
                const body = (await response.json()) as UsageResponse;
                const billingVisibleValue = Boolean(body.billingVisible);
                const hasInvoicesValue = Boolean(body.hasInvoices);
                const dashboardFlagsValue = body.dashboardFlags ?? DASHBOARD_FLAGS_OFF;
                const conversionContextValue = body.conversionContext ?? null;
                const warnAtPctValue = body.warnAtPct ?? DEFAULT_WARN_AT_PCT;
                setUsage(body.usage);
                setMonthlyHistory(body.monthlyHistory);
                setBillingVisible(billingVisibleValue);
                setHasInvoices(hasInvoicesValue);
                setDashboardFlags(dashboardFlagsValue);
                setConversionContext(conversionContextValue);
                setWarnAtPct(warnAtPctValue);
                if (conversionContextValue) {
                    setUserProperties({
                        plan: conversionContextValue.plan,
                    });
                }
                setDataUserId(user.id);
                dataUserIdRef.current = user.id;
                writeCachedUsage(user.id, {
                    usage: body.usage,
                    monthlyHistory: body.monthlyHistory,
                    billingVisible: billingVisibleValue,
                    hasInvoices: hasInvoicesValue,
                    dashboardFlags: dashboardFlagsValue,
                    conversionContext: conversionContextValue,
                    warnAtPct: warnAtPctValue,
                });
            })
            .catch(() => {
                if (!controller.signal.aborted && !hasDataToShow) {
                    setUsage(null);
                    setMonthlyHistory(null);
                    setBillingVisible(false);
                    setHasInvoices(false);
                    setDashboardFlags(DASHBOARD_FLAGS_OFF);
                    setConversionContext(null);
                    setWarnAtPct(DEFAULT_WARN_AT_PCT);
                    // Mark this user as resolved even though no usage payload
                    // was available, otherwise the user-facing loading state
                    // would remain true forever after a failed first request.
                    setDataUserId(user.id);
                    dataUserIdRef.current = user.id;
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            });

        return () => controller.abort();
    }, [
        isSessionChecked,
        user,
        refreshCount,
    ]);

    useEffect(() => {
        const refreshOnFocus = () => {
            setCheckoutStartedState(false);
            setRefreshCount((count) => count + 1);
        };
        const syncCheckoutTabs = (event: StorageEvent) => {
            if (isCheckoutSignal(event)) {
                setCheckoutStartedState(false);
                refreshOnFocus();
            }
        };
        window.addEventListener("focus", refreshOnFocus);
        window.addEventListener("storage", syncCheckoutTabs);
        return () => {
            window.removeEventListener("focus", refreshOnFocus);
            window.removeEventListener("storage", syncCheckoutTabs);
        };
    }, []);

    const setCheckoutStarted = useCallback((started: boolean) => {
        setCheckoutStartedState(started);
    }, []);

    const refresh = useCallback(() => {
        setCheckoutStarted(false);
        setRefreshCount((count) => count + 1);
    }, [
        setCheckoutStarted,
    ]);

    return (
        <UsageContext.Provider
            value={{
                usage: hasCurrentUserData ? usage : null,
                monthlyHistory: hasCurrentUserData ? monthlyHistory : null,
                loading: !isSessionChecked || (user !== null && !hasCurrentUserData) || loading,
                refresh,
                checkoutStarted,
                setCheckoutStarted,
                billingVisible: hasCurrentUserData ? billingVisible : false,
                hasInvoices: hasCurrentUserData ? hasInvoices : false,
                dashboardFlags: hasCurrentUserData ? dashboardFlags : DASHBOARD_FLAGS_OFF,
                conversionContext: hasCurrentUserData ? conversionContext : null,
                warnAtPct: hasCurrentUserData ? warnAtPct : DEFAULT_WARN_AT_PCT,
                usageWarningDismissedFor,
                setUsageWarningDismissedFor,
            }}
        >
            {children}
        </UsageContext.Provider>
    );
}
